/**
 * GIF Picker Component
 * Search and select GIFs from Tenor API
 *
 * Features:
 * - Search with debounce
 * - Trending GIFs on open
 * - Infinite scroll
 * - Preview on hover
 * - Portal-based rendering (escapes overflow-hidden parents)
 */

import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { gifService, type GifItem } from '@/services/gif.service';
import { Search, X, Loader2, ImageIcon } from 'lucide-react';
import { asApiError } from '@/utils/error.util';

interface GifPickerProps {
  onGifSelect: (gif: GifItem) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

const PICKER_WIDTH = 440;
const PICKER_MAX_HEIGHT = 420;

const GifPicker = ({ onGifSelect, onClose, anchorRef }: GifPickerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [gifs, setGifs] = useState<GifItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hoveredGif, setHoveredGif] = useState<GifItem | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const pickerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Calculate position based on anchor element
  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();

    // Right-align to button's right edge, place above
    let left = rect.right - PICKER_WIDTH;
    let top = rect.top - PICKER_MAX_HEIGHT - 8;

    // Clamp left edge
    if (left < 8) left = 8;

    // Clamp right edge
    if (left + PICKER_WIDTH > window.innerWidth - 8) {
      left = window.innerWidth - PICKER_WIDTH - 8;
    }

    // If not enough room above, place below
    if (top < 8) {
      top = rect.bottom + 8;
    }

    setPosition({ top, left });
  }, [anchorRef]);

  // Initial positioning
  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  // Reposition on resize
  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [updatePosition]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Don't close if clicking the anchor button (toggle handles that)
      if (anchorRef.current?.contains(target)) return;
      if (pickerRef.current && !pickerRef.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorRef]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Focus search input on open
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Load trending GIFs on mount
  useEffect(() => {
    loadTrending();
  }, []);

  const loadTrending = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await gifService.trending(1, 30);
      setGifs(result.data);
      setPage(1);
      setHasMore(result.data.length >= 30);
    } catch (err) {
      setError(asApiError(err).message || 'Грешка при зареждане на GIF');
      console.error('Failed to load trending GIFs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const searchGifs = useCallback(async (query: string, pageNum = 1) => {
    if (!query.trim()) {
      loadTrending();
      return;
    }

    setIsLoading(pageNum === 1);
    setError(null);

    try {
      const result = await gifService.search(query, pageNum, 30);

      if (pageNum === 1) {
        setGifs(result.data);
      } else {
        setGifs((prev) => [...prev, ...result.data]);
      }

      setPage(pageNum);
      setHasMore(result.data.length >= 30);
    } catch (err) {
      setError(asApiError(err).message || 'Грешка при търсене');
      console.error('GIF search failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchGifs(value, 1);
    }, 300);
  };

  // Infinite scroll
  const handleScroll = () => {
    if (!gridRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = gridRef.current;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      if (searchQuery) {
        searchGifs(searchQuery, page + 1);
      } else {
        loadMoreTrending();
      }
    }
  };

  const loadMoreTrending = async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const result = await gifService.trending(page + 1, 30);
      setGifs((prev) => [...prev, ...result.data]);
      setPage((prev) => prev + 1);
      setHasMore(result.data.length >= 30);
    } catch (err) {
      console.error('Failed to load more trending:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGifClick = (gif: GifItem) => {
    onGifSelect(gif);
    onClose();
  };

  const clearSearch = () => {
    setSearchQuery('');
    loadTrending();
    searchInputRef.current?.focus();
  };

  const picker = (
    <div
      ref={pickerRef}
      className="fixed bg-white dark:bg-discord-bg rounded-lg shadow-xl border border-gray-200 dark:border-discord-bg-deep flex flex-col overflow-hidden"
      style={{
        top: position.top,
        left: position.left,
        width: PICKER_WIDTH,
        maxHeight: PICKER_MAX_HEIGHT,
        zIndex: 9999,
      }}
    >
      {/* Header with Search */}
      <div className="p-3 border-b border-gray-200 dark:border-discord-bg-deep">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              role="combobox"
              aria-expanded={gifs.length > 0}
              aria-controls="gif-listbox"
              aria-label="Търси GIF"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Търси GIF..."
              className="w-full pl-9 pr-8 py-2 bg-gray-100 dark:bg-discord-bg-deep border-none rounded-md text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-discord-bg-hover rounded transition-colors"
              >
                <X className="w-3 h-3 text-gray-400" />
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-discord-bg-hover rounded transition-colors"
            aria-label="Затвори"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
          <ImageIcon className="w-3 h-3" />
          <span>{searchQuery ? 'Резултати от търсенето' : 'Trending GIFs'}</span>
          <span className="ml-auto text-[10px] opacity-60">Powered by Tenor</span>
        </div>
      </div>

      {/* GIF Grid */}
      <div
        ref={gridRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-2"
      >
        {error ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <p className="text-sm">{error}</p>
            <button
              onClick={() => searchQuery ? searchGifs(searchQuery, 1) : loadTrending()}
              className="mt-2 text-xs text-blue-500 hover:underline"
            >
              Опитай пак
            </button>
          </div>
        ) : gifs.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-500">
            <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
            <p className="text-sm">Няма намерени GIF</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1.5 auto-rows-[100px]" role="listbox" id="gif-listbox" aria-label="GIF резултати">
            {gifs.map((gif, index) => (
              <button
                key={`${gif.id}-${index}`}
                role="option"
                aria-selected={false}
                aria-label={gif.title}
                onClick={() => handleGifClick(gif)}
                onMouseEnter={() => setHoveredGif(gif)}
                onMouseLeave={() => setHoveredGif(null)}
                className="relative overflow-hidden rounded-md bg-gray-100 dark:bg-discord-bg-deep hover:ring-2 hover:ring-blue-500 transition-all group"
                title={gif.title}
              >
                <img
                  src={hoveredGif?.id === gif.id
                    ? gifService.getBestFormat(gif)
                    : gifService.getPreviewUrl(gif)
                  }
                  alt={gif.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </button>
            ))}
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(picker, document.body);
};

export default GifPicker;
